import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HorizontalBarChart } from '../../components/dashboard/HorizontalBarChart';
import { StatCard } from '../../components/dashboard/StatCard';
import { TrendBarChart } from '../../components/dashboard/TrendBarChart';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  ProspectionDashboard,
  fetchProspectionDashboard,
} from '../../services/prospectionDashboardApi';

const ENRICHMENT_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  InProgress: 'Em progresso',
  Completed: 'Concluído',
  Failed: 'Falhou',
  Skipped: 'Ignorado',
};

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function mapChartLabels(
  items: ProspectionDashboard['prospectsByEnrichmentStatus'],
  labels: Record<string, string>,
) {
  return items.map((item) => ({
    ...item,
    label: labels[item.label] ?? item.label,
  }));
}

export function ProspectionDashboardPage() {
  const { canAccessModule } = usePermissions();
  const [dashboard, setDashboard] = useState<ProspectionDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(false);
      try {
        setDashboard(await fetchProspectionDashboard());
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner label="Carregando painel de prospecção..." />;
  }

  if (error || !dashboard) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
        Não foi possível carregar o painel de prospecção. Tente novamente em instantes.
      </div>
    );
  }

  return (
    <PageShell variant="dashboard">
      <PageHeader
        title="Prospecção"
        description={`Coleta e enriquecimento de leads — atualizado em ${new Date(dashboard.generatedAtUtc).toLocaleString('pt-BR')}`}
        actions={
          canAccessModule('sales.prospects') ? (
            <Link to="/sales/prospects" className="erp-btn-primary">
              Ver prospects
            </Link>
          ) : undefined
        }
      />

      <section>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Prospects na base"
            value={formatNumber(dashboard.prospectsTotal)}
            hint={`+${dashboard.prospectsThisMonth} neste mês`}
            tone="primary"
          />
          <StatCard label="Campanhas ativas" value={formatNumber(dashboard.campaignsTotal)} tone="sky" />
          <StatCard
            label="Enriquecimento pendente"
            value={formatNumber(dashboard.prospectsPendingEnrichment)}
            hint="Aguardando ou em processamento"
            tone="orange"
          />
          <StatCard label="Enriquecidos" value={formatNumber(dashboard.prospectsEnriched)} tone="emerald" />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Topo do funil"
            value={formatNumber(dashboard.funnelCaptured)}
            hint="Capturados"
            tone="violet"
          />
          <StatCard
            label="Leads"
            value={formatNumber(dashboard.funnelLeads)}
            hint="Com e-mail validado"
            tone="indigo"
          />
          <StatCard label="Perdidos" value={formatNumber(dashboard.funnelLost)} tone="rose" />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <TrendBarChart title="Coleta (últimos 7 dias)" points={dashboard.collectionTrend} />
        <TrendBarChart title="Enriquecimento (últimos 7 dias)" points={dashboard.enrichmentTrend} />
        <HorizontalBarChart
          title="Prospects por campanha"
          items={dashboard.prospectsByCampaign}
          valueFormatter={(value) => formatNumber(value)}
        />
        <HorizontalBarChart
          title="Status de enriquecimento"
          items={mapChartLabels(dashboard.prospectsByEnrichmentStatus, ENRICHMENT_LABELS)}
          valueFormatter={(value) => formatNumber(value)}
        />
      </section>
    </PageShell>
  );
}
